import { Controller } from "@hotwired/stimulus"

// A figure page's video reviews (figures/show): a video opens in a pop-up
// player over the page (YouTube's embed, from its no-cookie domain), with
// the creator's credit under it -- their channel, the video's numbers on
// YouTube (YoutubeStats), and the ways to back them there: subscribing,
// liking or commenting. When the video ends, a thank-you card asks again.
//
// It opens paused, for YouTube's own play button to start: YouTube doesn't
// count a view a player started by itself, and the creator should get it.
//
// A video whose owner won't let it play on other sites says so in the
// player, with the way to it on YouTube. A click meant for a new tab (or
// with no script) still follows the link to YouTube.
//
// Arriving with ?play=<video> (home's verdict cards link so, to #reviews)
// opens that video's player straight away; the address then drops ?play,
// so going back or reloading doesn't open it again.
export default class extends Controller {
  connect() {
    const url = new URL(window.location.href)
    const video = url.searchParams.get("play")
    if (!video || !/^[\w-]{11}$/.test(video)) return

    url.searchParams.delete("play")
    history.replaceState(history.state, "", url)
    const tile = this.element.querySelector(`[data-review-player-video-param="${video}"]`)
    this.show(tile ? paramsOf(tile) : { video }, tile?.href || `https://www.youtube.com/watch?v=${video}`)
  }

  open(event) {
    if (!event.params.video) return
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    event.preventDefault()
    this.show(event.params, event.currentTarget.href)
  }

  // params: the tile's video, title, channel, subscribe (link), stats and
  // subscribers (both already worded, "20K views · 71 comments").
  show(params, url) {
    const dialog = this.dialog()
    const set = (selector, text) => { const el = dialog.querySelector(selector); el.textContent = text || ""; el.hidden = !text }
    set(".fg-player-title", params.title)
    set(".fg-player-stats", params.stats)
    set(".fg-player-channel", params.channel)
    set(".fg-player-subs", params.subscribers)
    dialog.querySelector(".fg-player-by").hidden = !params.channel
    dialog.querySelectorAll(".fg-player-sub").forEach((a) => { a.hidden = !params.subscribe; if (params.subscribe) a.href = params.subscribe })
    dialog.querySelectorAll(".fg-player-out").forEach((a) => { a.href = url })
    dialog.querySelector(".fg-player-thanks-who").textContent = params.channel ? `${params.channel}’s` : "this"
    dialog.querySelector(".fg-player-blocked").hidden = true
    dialog.querySelector(".fg-player-thanks").hidden = true
    dialog.querySelector(".fg-player-frame").hidden = false
    dialog.showModal()
    this.play(params.video)
  }

  disconnect() {
    this.stop()
    this.player = null
    this.modal?.remove()
    this.modal = null
  }

  // The player's box, made once and kept at the end of the page.
  dialog() {
    if (this.modal) return this.modal
    const dialog = document.createElement("dialog")
    dialog.className = "fg-player"
    dialog.setAttribute("aria-label", "Video review")
    dialog.innerHTML = `
      <div class="fg-player-box">
        <button type="button" class="fg-player-close" aria-label="Close">&times;</button>
        <div class="fg-player-screen">
          <div class="fg-player-frame"></div>
          <div class="fg-player-blocked" hidden>
            <b>This one only plays on YouTube.</b>
            <span>It can't play here, but the link below takes you to it.</span>
          </div>
          <div class="fg-player-thanks" hidden>
            <b>Thanks for watching <span class="fg-player-thanks-who"></span> review!</b>
            <span>Reviewers make these for fans like us. Show them some love on YouTube.</span>
            <div class="fg-player-thanks-acts">
              <a class="fg-player-sub" target="_blank" rel="noopener"><i class="fab fa-youtube" aria-hidden="true"></i> Subscribe</a>
              <a class="fg-player-out is-ghost" target="_blank" rel="noopener"><i class="fas fa-thumbs-up" aria-hidden="true"></i> Like or comment</a>
              <button type="button" class="fg-player-again is-ghost"><i class="fas fa-rotate-left" aria-hidden="true"></i> Watch again</button>
            </div>
          </div>
        </div>
        <div class="fg-player-foot">
          <div class="fg-player-about">
            <span class="fg-player-title"></span>
            <span class="fg-player-stats"></span>
          </div>
          <div class="fg-player-credit">
            <span class="fg-player-by">Review by <b class="fg-player-channel"></b> <span class="fg-player-subs"></span></span>
            <span class="fg-player-acts">
              <a class="fg-player-sub" target="_blank" rel="noopener"><i class="fab fa-youtube" aria-hidden="true"></i> Subscribe</a>
              <a class="fg-player-out" target="_blank" rel="noopener">Like or comment on YouTube <i class="fas fa-arrow-up-right-from-square" aria-hidden="true"></i></a>
            </span>
          </div>
        </div>
      </div>`
    dialog.querySelector(".fg-player-close").addEventListener("click", () => dialog.close())
    dialog.querySelector(".fg-player-again").addEventListener("click", () => this.again())
    // Clicks on the dialog itself (not its box) are backdrop clicks.
    dialog.addEventListener("click", (e) => { if (e.target === dialog) dialog.close() })
    dialog.addEventListener("close", () => this.stop())
    document.body.appendChild(dialog)
    return (this.modal = dialog)
  }

  async play(video) {
    try {
      await youtubeApi()
    } catch {
      return this.blocked() // the player script didn't load (an ad blocker): YouTube it is
    }
    if (!this.modal?.open) return
    const slot = document.createElement("div")
    this.modal.querySelector(".fg-player-frame").replaceChildren(slot)
    this.player = new window.YT.Player(slot, {
      host: "https://www.youtube-nocookie.com",
      videoId: video,
      playerVars: { rel: 0, playsinline: 1 },
      events: {
        // 101 and 150: its owner doesn't allow embedding. Anything else (gone,
        // private) also can't play here; YouTube's own page explains.
        onError: () => this.blocked(),
        onStateChange: (e) => { if (e.data === window.YT.PlayerState.ENDED) this.ended() }
      }
    })
  }

  blocked() {
    if (!this.modal) return
    this.modal.querySelector(".fg-player-frame").hidden = true
    this.modal.querySelector(".fg-player-blocked").hidden = false
  }

  // The video's over: the thank-you card, over the player.
  ended() {
    this.modal?.querySelector(".fg-player-thanks")?.removeAttribute("hidden")
  }

  again() {
    this.modal.querySelector(".fg-player-thanks").hidden = true
    this.player?.seekTo?.(0)
    this.player?.playVideo?.()
  }

  // Closing stops the video: the player goes, and the next one starts fresh.
  stop() {
    this.player?.destroy?.()
    this.player = null
    this.modal?.querySelector(".fg-player-frame")?.replaceChildren()
  }
}

// A tile's player params (its data-review-player-*-param attributes), as
// Stimulus gives an action's event.params.
function paramsOf(el) {
  const params = {}
  for (const [key, value] of Object.entries(el.dataset)) {
    const name = key.match(/^reviewPlayer(\w+)Param$/)?.[1]
    if (name) params[name[0].toLowerCase() + name.slice(1)] = value
  }
  return params
}

// YouTube's player script (https://developers.google.com/youtube/iframe_api_reference),
// loaded once, the first time a video is opened.
let apiLoading
function youtubeApi() {
  if (window.YT?.Player) return Promise.resolve()
  apiLoading ||= new Promise((resolve, reject) => {
    const ready = window.onYouTubeIframeAPIReady
    window.onYouTubeIframeAPIReady = () => { ready?.(); resolve() }
    const script = document.createElement("script")
    script.src = "https://www.youtube.com/iframe_api"
    script.onerror = () => { apiLoading = null; reject(new Error("YouTube player didn't load")) }
    document.head.appendChild(script)
  })
  return apiLoading
}
